import { useCallback, useRef, useState } from "react";

const JOB_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
const STORAGE_KEY_PREFIX = "Stitch-document-jobs-";

export interface DocumentJob {
  jobId: string;
  filename: string;
  fileSize?: number;
  createdAt: number;
  failed?: boolean;
}

function storageKey(patientId: number) {
  return `${STORAGE_KEY_PREFIX}${patientId}`;
}

function loadJobs(patientId: number): DocumentJob[] {
  try {
    const raw = localStorage.getItem(storageKey(patientId));
    if (!raw) return [];
    const parsed: DocumentJob[] = JSON.parse(raw);
    const cutoff = Date.now() - JOB_MAX_AGE_MS;
    return parsed.filter((j) => j.createdAt > cutoff);
  } catch {
    return [];
  }
}

function saveJobs(patientId: number, jobs: DocumentJob[]) {
  localStorage.setItem(storageKey(patientId), JSON.stringify(jobs));
}

export function useDocumentJobs(patientId: number) {
  const [jobs, setJobs] = useState<DocumentJob[]>(() => loadJobs(patientId));
  const jobsRef = useRef(jobs);

  const addJob = useCallback(
    (jobId: string, filename: string, fileSize?: number) => {
      const newJob: DocumentJob = {
        jobId,
        filename,
        fileSize,
        createdAt: Date.now(),
      };
      const next = [...jobsRef.current, newJob];
      jobsRef.current = next;
      saveJobs(patientId, next);
      setJobs(next);
    },
    [patientId],
  );

  const markJobFailed = useCallback(
    (jobId: string) => {
      const next = jobsRef.current.map((j) =>
        j.jobId === jobId ? { ...j, failed: true } : j,
      );
      jobsRef.current = next;
      // Remove from localStorage — there is no server-side job to poll.
      saveJobs(
        patientId,
        next.filter((j) => !j.failed),
      );
      setJobs(next);
    },
    [patientId],
  );

  const removeJob = useCallback(
    (jobId: string) => {
      const next = jobsRef.current.filter((j) => j.jobId !== jobId);
      jobsRef.current = next;
      saveJobs(patientId, next);
      setJobs(next);
    },
    [patientId],
  );

  return { jobs, addJob, markJobFailed, removeJob };
}
